import { Grid, IconButton, Paper, Typography } from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import PageContainer from "../containers/PageContainer";
import PageTitleCard from "../cards/PageTitleCard";
import PageTitleText from "../texts/PageTitleText";
import { useTranslation } from "react-i18next";
import useFetch from "../hooks/useFetch";
import UserContext from "../contexts/UserContext";
import { getUserStatistics } from "../config/apiRoutes";
import RefreshIcon from '@mui/icons-material/Refresh';
import { DailyScreenViews, } from "../models/Statistics";
import UserTrafficChart from "../statistics/UserTrafficChart";
import SnackbarContext from "../contexts/SnackbarContext";
import BigLoader from "../common/BigLoader";
import PrimaryButtonGroup from "../buttons/PrimaryButtonGroup";
import { hasRole } from "../models/User";
import { RoleType } from "../models/Role";
import PrimaryButton from "../buttons/PrimaryButton";
import { useNavigate, useParams } from "react-router";
import { paymentSettingsRoute } from "../config/pageRoutes";
import MenuLikeStatisticTable from "../statistics/MenuLikeStatisticTable";
import ClickCountTable from "../statistics/ClickCountTable";

const StatisticsPage: React.FC = () => {
    const { t } = useTranslation();
    const { lang } = useParams();
    const userCaller = useFetch();
    const { selectedRestaurant, user, hasFeature } = useContext(UserContext);
    const [userTrafficStatistics, setUserTrafficStatistics] = useState<DailyScreenViews | null>(null);
    const [userTrafficPeriod, setUserTrafficPeriod] = useState<'last7days' | 'last30days'>('last30days');
    const snackbar = useContext(SnackbarContext);
    const navigate = useNavigate()

    const redirectToPayment = () => {
        navigate(paymentSettingsRoute.path.replace(':lang', lang || 'en'));
    };

    const hasAccess = hasRole(user, RoleType.OWNER) && hasFeature("ANALYTICS_INSIGHTS");

    useEffect(() => {
        if (selectedRestaurant && hasAccess) {
            fetchUserStatistics();
        }
    }, [selectedRestaurant]);

    async function fetchUserStatistics() {
        const url = getUserStatistics.replace(":restaurantShortName", selectedRestaurant?.restaurantShortName || "");
        const response = await userCaller.call<undefined, DailyScreenViews>("GET", url);
        if (response.ok && response.data) {
            setUserTrafficStatistics(response.data);
        } else {
            snackbar.showMessage(t("statistics.error_fetching_user_statistics"), "error");
        }
    }

    function handleUserTrafficPeriodChange(period: 'last7days' | 'last30days') {
        if (period === userTrafficPeriod) return;
        setUserTrafficPeriod(period);
    }

    return (
        <PageContainer>
            <PageTitleCard>
                <div className="flex justify-between" >
                    <PageTitleText>
                        {t("statistics.statistics")}
                    </PageTitleText>
                </div>
            </PageTitleCard>
            {!hasAccess ?
                hasRole(user, RoleType.OWNER) ? (
                    <div className="flex flex-col items-center justify-center mt-20 gap-4">
                        <p className="text-lg mb-4 text-primary-dark">
                            {t("statistics.statistics_feature_not_available")}
                        </p>
                        <PrimaryButton
                            variant="primary"
                            onClick={redirectToPayment}
                        >
                            {t('statistics.change_subscription_plan')}
                        </PrimaryButton>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center mt-20 gap-4">
                        <p className="text-lg mb-4">
                            {t("statistics.permission_denied")}
                        </p>
                    </div>
                )
                : (
                    <div className="mt-4 flex flex-col gap-4 mb-2">
                        <Paper elevation={2} className="p-4">
                            <Grid container>
                                <Grid item xs>
                                    <div className="pb-4 pt-1 text-xl font-bold text-primary-dark">
                                        {t("statistics.user_traffic")}
                                    </div>
                                </Grid>
                                <Grid item>
                                    <IconButton onClick={fetchUserStatistics} title={t("common.refresh")}>
                                        <RefreshIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                            <div className="mb-2">
                                <PrimaryButtonGroup
                                    variant="outlined"
                                    fullWidthOnMobile={true}
                                    buttons={[
                                        { label: t("statistics.last_7_days"), onClick: () => handleUserTrafficPeriodChange('last7days'), isSelected: userTrafficPeriod === 'last7days' },
                                        { label: t("statistics.last_30_days"), onClick: () => handleUserTrafficPeriodChange('last30days'), isSelected: userTrafficPeriod === 'last30days' },
                                    ]}
                                />
                            </div>
                            {userCaller.loading ? (
                                <BigLoader />
                            ) : (
                                <UserTrafficChart data={userTrafficStatistics || { last7days: [], last30days: [] }} period={userTrafficPeriod} />
                            )}
                        </Paper>
                        <Paper elevation={2} className="p-4">
                            <ClickCountTable />
                        </Paper>
                        <Paper elevation={2} className="p-4">
                            <Grid container>
                                <Grid item xs>
                                    <div className="pb-4 pt-1 text-xl font-bold text-primary-dark">
                                        {t("statistics.menu_item_ratings")}
                                    </div>
                                </Grid>
                            </Grid>
                            <MenuLikeStatisticTable />
                        </Paper>
                    </div>
                )}
        </PageContainer>
    );
};

export default StatisticsPage;